<template>
  <div class="advertiser-report">
    <div class="page-toolbar">
      <div class="title-block">
        <h2>广告主报表</h2>
        <div class="meta-line">
          <el-tag size="mini" type="info" effect="plain">广告主层级</el-tag>
          <el-tag size="mini" :type="isSavedData ? 'warning' : 'primary'" effect="plain">{{ dataSourceText }}</el-tag>
          <el-tag size="mini" type="success" effect="plain">{{ dateModeText }}</el-tag>
          <el-tag size="mini" type="info" effect="plain">{{ reportTimeZoneText }}</el-tag>
          <span>{{ reportPeriodText }}</span>
          <span>{{ queryScopeText }}</span>
        </div>
      </div>
      <div class="toolbar-actions">
        <el-button
          type="warning"
          icon="el-icon-upload2"
          :loading="syncing"
          @click="syncLast14Days"
        >
          同步最近14天
        </el-button>
        <el-button
          type="primary"
          icon="el-icon-refresh"
          :loading="loading"
          @click="handleQuery"
        >
          刷新数据
        </el-button>
      </div>
    </div>

    <el-card shadow="never" class="filter-card">
      <el-form :model="queryForm" :rules="rules" ref="queryForm" inline class="filter-form">
        <el-form-item label="数据来源">
          <el-radio-group v-model="queryForm.dataSource" size="small" @change="handleDataSourceChange">
            <el-radio-button label="live">实时查询</el-radio-button>
            <el-radio-button label="saved">已保存数据</el-radio-button>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="广告主">
          <el-select
            v-model="queryForm.advertiserIds"
            multiple
            collapse-tags
            filterable
            clearable
            placeholder="默认查询全部广告主"
            style="width: 360px"
          >
            <el-option
              v-for="item in advertiserList"
              :key="item.advertiser_id"
              :label="formatAdvertiserOption(item)"
              :value="item.advertiser_id"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="开始日期" prop="startDate">
          <el-date-picker
            v-model="queryForm.startDate"
            type="date"
            placeholder="选择开始日期"
            value-format="yyyy-MM-dd"
            style="width: 160px"
          />
        </el-form-item>

        <el-form-item label="结束日期" prop="endDate">
          <el-date-picker
            v-model="queryForm.endDate"
            type="date"
            placeholder="选择结束日期"
            value-format="yyyy-MM-dd"
            style="width: 160px"
          />
        </el-form-item>

        <el-form-item label="日期维度">
          <el-radio-group v-model="queryForm.dateGroupMode" size="small" :disabled="isSavedData">
            <el-radio-button label="summary">不分日期</el-radio-button>
            <el-radio-button label="day">按日期</el-radio-button>
          </el-radio-group>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleQuery" :loading="loading">
            查询
          </el-button>
          <el-button icon="el-icon-refresh-left" @click="resetForm">重置</el-button>
          <el-button
            type="success"
            icon="el-icon-download"
            @click="exportData"
            :disabled="tableData.length === 0"
          >
            导出数据
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-row :gutter="12" class="stats-row">
      <el-col :xs="24" :sm="12" :md="8" :lg="4">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-item">
            <div class="stat-label">{{ primaryCountLabel }}</div>
            <div class="stat-value">{{ pagination.total }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="8" :lg="4">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-item">
            <div class="stat-label">总消耗</div>
            <div class="stat-value">${{ formatMoney(statsData.totalSpend) }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="8" :lg="4">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-item">
            <div class="stat-label">净消耗</div>
            <div class="stat-value">${{ formatMoney(statsData.totalBilledCost) }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="8" :lg="4">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-item">
            <div class="stat-label">现金 / 赠款</div>
            <div class="stat-value small">${{ formatMoney(statsData.totalCashSpend) }} / ${{ formatMoney(statsData.totalVoucherSpend) }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="8" :lg="4">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-item">
            <div class="stat-label">第0天广告收入</div>
            <div class="stat-value">${{ formatMoney(statsData.totalRevenueDay0) }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="8" :lg="4">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-item">
            <div class="stat-label">第13天广告收入</div>
            <div class="stat-value">${{ formatMoney(statsData.totalRevenueDay13) }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="8" :lg="4">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-item">
            <div class="stat-label">展示 / 点击</div>
            <div class="stat-value small">{{ formatNumber(statsData.totalImpressions) }} / {{ formatNumber(statsData.totalClicks) }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="8" :lg="4">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-item">
            <div class="stat-label">转化 / CPA</div>
            <div class="stat-value small">{{ formatNumber(statsData.totalConversion) }} / ${{ formatMoney(statsData.avgCpa) }}</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-card shadow="never" class="table-card">
      <div class="table-header">
        <div>
          <span class="table-title">广告主明细</span>
          <el-tag size="mini" effect="plain" class="table-tag">{{ tableModeLabel }}</el-tag>
        </div>
        <div class="table-actions">
          <span class="muted-text">当前页 {{ pagedTableData.length }} 条</span>
        </div>
      </div>

      <el-table
        class="report-table"
        :data="pagedTableData"
        v-loading="loading"
        :element-loading-text="loadingText"
        stripe
        show-summary
        :summary-method="getSummaryRow"
        empty-text="暂无报表数据"
        max-height="700"
        style="width: 100%"
        row-key="rowKey"
        :default-sort="{prop: 'spend', order: 'descending'}"
        @sort-change="handleSortChange"
      >
        <el-table-column label="广告主" min-width="240" fixed>
          <template slot-scope="scope">
            <div class="advertiser-info">
              <div class="advertiser-name">{{ scope.row.advertiserName }}</div>
              <div class="advertiser-id">{{ scope.row.advertiserId }}</div>
            </div>
          </template>
        </el-table-column>

        <el-table-column v-if="isSplitByDate || isSavedData" prop="statTimeDay" label="日期" width="120" align="center" :sortable="isSavedData ? 'custom' : true" />

        <el-table-column label="消耗" width="120" align="right" :sortable="isSavedData ? 'custom' : true" prop="spend">
          <template slot-scope="scope">
            <span class="metric-value spend">${{ formatMoney(scope.row.spend) }}</span>
          </template>
        </el-table-column>

        <el-table-column label="净消耗" width="120" align="right" :sortable="isSavedData ? 'custom' : true" prop="billedCost">
          <template slot-scope="scope">
            <span class="metric-value spend">${{ formatMoney(scope.row.billedCost) }}</span>
          </template>
        </el-table-column>

        <el-table-column label="现金消耗" width="120" align="right" :sortable="isSavedData ? 'custom' : true" prop="cashSpend">
          <template slot-scope="scope">
            ${{ formatMoney(scope.row.cashSpend) }}
          </template>
        </el-table-column>

        <el-table-column label="赠款消耗" width="120" align="right" :sortable="isSavedData ? 'custom' : true" prop="voucherSpend">
          <template slot-scope="scope">
            ${{ formatMoney(scope.row.voucherSpend) }}
          </template>
        </el-table-column>

        <el-table-column label="展示次数" width="120" align="right" :sortable="isSavedData ? 'custom' : true" prop="impressions">
          <template slot-scope="scope">
            {{ formatNumber(scope.row.impressions) }}
          </template>
        </el-table-column>

        <el-table-column label="点击次数" width="110" align="right" :sortable="isSavedData ? 'custom' : true" prop="clicks">
          <template slot-scope="scope">
            {{ formatNumber(scope.row.clicks) }}
          </template>
        </el-table-column>

        <el-table-column label="点击率" width="100" align="right" :sortable="isSavedData ? 'custom' : true" prop="ctr">
          <template slot-scope="scope">
            {{ formatPercent(scope.row.ctr) }}
          </template>
        </el-table-column>

        <el-table-column label="转化数" width="100" align="right" :sortable="isSavedData ? 'custom' : true" prop="conversion">
          <template slot-scope="scope">
            {{ formatNumber(scope.row.conversion) }}
          </template>
        </el-table-column>

        <el-table-column label="CPA" width="100" align="right" :sortable="isSavedData ? 'custom' : true" prop="cpa">
          <template slot-scope="scope">
            ${{ formatMoney(scope.row.cpa) }}
          </template>
        </el-table-column>

        <el-table-column label="第0天广告收入" width="150" align="right" :sortable="isSavedData ? 'custom' : true" prop="revenueDay0">
          <template slot-scope="scope">
            <span class="metric-value revenue">${{ formatMoney(scope.row.revenueDay0) }}</span>
          </template>
        </el-table-column>

        <el-table-column label="第13天广告收入" width="150" align="right" :sortable="isSavedData ? 'custom' : true" prop="revenueDay13">
          <template slot-scope="scope">
            <span class="metric-value revenue">${{ formatMoney(scope.row.revenueDay13) }}</span>
          </template>
        </el-table-column>

        <el-table-column label="第0天ROAS" width="110" align="right" :sortable="isSavedData ? 'custom' : true" prop="roasDay0">
          <template slot-scope="scope">
            {{ formatDecimal(scope.row.roasDay0, 2) }}
          </template>
        </el-table-column>

        <el-table-column label="第13天ROAS" width="120" align="right" :sortable="isSavedData ? 'custom' : true" prop="roasDay13">
          <template slot-scope="scope">
            {{ formatDecimal(scope.row.roasDay13, 2) }}
          </template>
        </el-table-column>

        <el-table-column v-if="isSavedData" label="1-14日ROI" align="center">
          <el-table-column
            v-for="day in attributionDays"
            :key="`roas-${day}`"
            :prop="`roasDay${day}`"
            :label="`${day}日ROI`"
            width="96"
            align="right"
            sortable="custom"
          >
            <template slot-scope="scope">
              {{ formatDecimal(scope.row[`roasDay${day}`], 2) }}
            </template>
          </el-table-column>
        </el-table-column>
      </el-table>

      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.page"
        :page-sizes="[10, 20, 50, 100, 200]"
        :page-size="pagination.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total"
        class="table-pagination"
      />
    </el-card>
  </div>
</template>

<script>
import { reportApi } from '@/api/report'
import { advertiserApi } from '@/api/advertiserApi'
import { queryAdvertiserDailyReports, syncAdvertiserDailyReports } from '@/api/tiktokAdvertiserDailyReport'

const REPORT_METRICS = [
  'spend',
  'billed_cost',
  'cash_spend',
  'voucher_spend',
  'impressions',
  'clicks',
  'conversion',
  'onsite_ad_impression_ad_revenue_day0',
  'onsite_ad_impression_ad_revenue_day13',
  'onsite_ad_impression_ad_revenue_roas_day0',
  'onsite_ad_impression_ad_revenue_roas_day13'
]

export default {
  name: 'AdvertiserReport',
  data() {
    return {
      loading: false,
      syncing: false,
      advertiserList: [],
      queryForm: {
        dataSource: 'live',
        advertiserIds: [],
        startDate: this.getDefaultStartDate(),
        endDate: this.getDefaultEndDate(),
        dateGroupMode: 'summary'
      },
      rules: {
        startDate: [
          { required: true, message: '请选择开始日期', trigger: 'change' }
        ],
        endDate: [
          { required: true, message: '请选择结束日期', trigger: 'change' }
        ]
      },
      tableData: [],
      statsData: {
        totalSpend: 0,
        totalBilledCost: 0,
        totalCashSpend: 0,
        totalVoucherSpend: 0,
        totalRevenueDay0: 0,
        totalRevenueDay13: 0,
        totalImpressions: 0,
        totalClicks: 0,
        totalConversion: 0,
        avgCpa: 0
      },
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      sortState: {
        field: '',
        order: ''
      }
    }
  },
  computed: {
    advertiserNameMap() {
      return this.advertiserList.reduce((map, item) => {
        map[item.advertiser_id] = item.advertiser_name || item.advertiser_id
        return map
      }, {})
    },
    pagedTableData() {
      if (this.isSavedData) {
        return this.tableData
      }
      const start = (this.pagination.page - 1) * this.pagination.pageSize
      return this.tableData.slice(start, start + this.pagination.pageSize)
    },
    isSavedData() {
      return this.queryForm.dataSource === 'saved'
    },
    isSplitByDate() {
      return this.queryForm.dateGroupMode === 'day' || this.isSavedData
    },
    dataSourceText() {
      return this.isSavedData ? '已保存数据' : '实时查询'
    },
    dateModeText() {
      return this.isSplitByDate ? '按日期' : '不分日期'
    },
    tableModeLabel() {
      return this.isSplitByDate ? '按日期拆分' : '周期汇总'
    },
    primaryCountLabel() {
      return this.isSplitByDate ? '记录数' : '广告主数'
    },
    reportPeriodText() {
      return `${this.queryForm.startDate || '-'} 至 ${this.queryForm.endDate || '-'}`
    },
    reportTimeZoneText() {
      return '美东口径，每日 01:30 自动保存'
    },
    queryScopeText() {
      const selectedCount = (this.queryForm.advertiserIds || []).length
      if (selectedCount > 0) return `已选 ${selectedCount} 个广告主`
      return `全部广告主 ${this.advertiserList.length} 个`
    },
    loadingText() {
      const count = this.getTargetAdvertiserIds().length
      if (this.isSavedData) return '正在查询已保存日报'
      return count > 0 ? `正在查询 ${count} 个广告主` : '正在查询报表数据'
    },
    attributionDays() {
      return Array.from({ length: 14 }, (_, index) => index + 1)
    }
  },
  created() {
    this.loadAdvertiserList()
  },
  methods: {
    async loadAdvertiserList() {
      try {
        const response = await advertiserApi.getList()
        if (response.code === 0 || response.success) {
          this.advertiserList = response.data?.list || []
        } else {
          this.$message.error(response.message || '加载广告主列表失败')
        }
      } catch (error) {
        this.$message.error('加载广告主列表失败: ' + error.message)
      }
    },
    handleQuery() {
      this.pagination.page = 1
      this.loadData()
    },
    loadData() {
      this.$refs.queryForm.validate(async valid => {
        if (!valid) return

        if (this.isSavedData) {
          await this.loadSavedData()
          return
        }

        const advertiserIds = this.getTargetAdvertiserIds()
        if (advertiserIds.length === 0) {
          this.$message.warning('没有可查询的广告主')
          return
        }

        this.loading = true
        try {
          const chunks = this.chunkArray(advertiserIds, 5)
          const responses = await Promise.all(
            chunks.map(chunk => reportApi.getIntegratedReport(this.buildReportPayload(chunk)))
          )
          const rows = responses.reduce((result, response) => {
            return result.concat(this.normalizeReportRows(response))
          }, [])

          this.tableData = rows
            .filter(row => row.advertiserId)
            .sort(this.sortReportRows)
          this.pagination.total = this.tableData.length
          this.statsData = this.calculateStats(this.tableData)
          this.$message.success(`成功获取 ${this.tableData.length} 个广告主数据`)
        } catch (error) {
          this.$message.error('查询失败: ' + error.message)
        } finally {
          this.loading = false
        }
      })
    },
    async loadSavedData() {
      this.loading = true
      try {
        const response = await queryAdvertiserDailyReports({
          advertiserIds: this.queryForm.advertiserIds,
          startDate: this.queryForm.startDate,
          endDate: this.queryForm.endDate,
          sortField: this.backendSortField(this.sortState.field),
          sortOrder: this.sortState.order,
          page: this.pagination.page - 1,
          size: this.pagination.pageSize
        })
        const pageData = response.data || {}
        this.tableData = (pageData.content || []).map(this.normalizeSavedReportRow)
        this.pagination.total = Number(pageData.totalElements || 0)
        this.statsData = this.calculateStats(this.tableData)
      } catch (error) {
        this.$message.error('查询已保存数据失败: ' + error.message)
      } finally {
        this.loading = false
      }
    },
    normalizeSavedReportRow(item, index) {
      const row = {
        rowKey: `${item.advertiserId || `row-${index}`}-${item.statDate || ''}`,
        advertiserId: item.advertiserId || '',
        statTimeDay: item.statDate || '',
        advertiserName: item.advertiserName || this.getAdvertiserName(item.advertiserId),
        spend: this.toNumber(item.spend),
        billedCost: this.toNumber(item.billedCost),
        cashSpend: this.toNumber(item.cashSpend),
        voucherSpend: this.toNumber(item.voucherSpend),
        impressions: this.toNumber(item.impressions),
        clicks: this.toNumber(item.clicks),
        ctr: this.toNumber(item.ctr),
        conversion: this.toNumber(item.conversion),
        cpa: this.toNumber(item.cpa),
        revenueDay0: this.toNumber(item.revenueDay0),
        roasDay0: this.toNumber(item.roasDay0),
        revenueDay13: this.toNumber(item.revenueDay13),
        roasDay13: this.toNumber(item.roasDay13)
      }
      this.attributionDays.forEach(day => {
        row[`revenueDay${day}`] = this.toNumber(item[`revenueDay${day}`])
        row[`roasDay${day}`] = this.toNumber(item[`roasDay${day}`])
      })
      return row
    },
    async syncLast14Days() {
      this.syncing = true
      try {
        const response = await syncAdvertiserDailyReports({
          advertiserIds: this.queryForm.advertiserIds,
          days: 14
        })
        const result = response.data || {}
        this.$message.success(`同步完成：新增 ${result.inserted || 0} 条，更新 ${result.updated || 0} 条`)
        if (this.isSavedData) {
          this.pagination.page = 1
          await this.loadSavedData()
        }
      } catch (error) {
        this.$message.error('同步失败: ' + error.message)
      } finally {
        this.syncing = false
      }
    },
    buildReportPayload(advertiserIds) {
      const payload = {
        serviceType: 'AUCTION',
        reportType: 'BASIC',
        dataLevel: 'AUCTION_ADVERTISER',
        dimensions: this.isSplitByDate ? ['advertiser_id', 'stat_time_day'] : ['advertiser_id'],
        metrics: REPORT_METRICS,
        startDate: this.queryForm.startDate,
        endDate: this.queryForm.endDate,
        enableTotalMetrics: true,
        page: 1,
        pageSize: 1000
      }

      if (advertiserIds.length === 1) {
        payload.advertiserId = advertiserIds[0]
      } else {
        payload.advertiserIds = advertiserIds
        payload.multiAdvReportInUtcTime = false
      }

      return payload
    },
    normalizeReportRows(response) {
      const body = response && response.data ? response.data : {}
      if (body.code !== 0 && body.code !== undefined) {
        throw new Error(body.message || 'TikTok 报表接口返回失败')
      }

      const reportData = body.data || {}
      const list = reportData.list || []
      return list.map((item, index) => this.normalizeReportRow(item, index))
    },
    normalizeReportRow(item, index) {
      const dimensions = item.dimensions || {}
      const metrics = item.metrics || {}
      const advertiserId = String(dimensions.advertiser_id || metrics.advertiser_id || '')
      const statTimeDay = String(dimensions.stat_time_day || metrics.stat_time_day || '')
      const spend = this.toNumber(metrics.spend)
      const billedCost = this.toNumber(metrics.billed_cost)
      const cashSpend = this.toNumber(metrics.cash_spend)
      const voucherSpend = this.toNumber(metrics.voucher_spend)
      const impressions = this.toNumber(metrics.impressions)
      const clicks = this.toNumber(metrics.clicks)
      const conversion = this.toNumber(metrics.conversion)

      return {
        rowKey: `${advertiserId || `row-${index}`}-${statTimeDay || 'summary'}`,
        advertiserId,
        statTimeDay,
        advertiserName: this.getAdvertiserName(advertiserId),
        spend,
        billedCost,
        cashSpend,
        voucherSpend,
        impressions,
        clicks,
        ctr: impressions > 0 ? clicks / impressions : 0,
        conversion,
        cpa: conversion > 0 ? spend / conversion : 0,
        revenueDay0: this.toNumber(metrics.onsite_ad_impression_ad_revenue_day0),
        revenueDay13: this.toNumber(metrics.onsite_ad_impression_ad_revenue_day13),
        roasDay0: this.toNumber(metrics.onsite_ad_impression_ad_revenue_roas_day0),
        roasDay13: this.toNumber(metrics.onsite_ad_impression_ad_revenue_roas_day13)
      }
    },
    calculateStats(rows) {
      const stats = rows.reduce((stats, row) => {
        stats.totalSpend += row.spend
        stats.totalBilledCost += row.billedCost
        stats.totalCashSpend += row.cashSpend
        stats.totalVoucherSpend += row.voucherSpend
        stats.totalRevenueDay0 += row.revenueDay0
        stats.totalRevenueDay13 += row.revenueDay13
        stats.totalImpressions += row.impressions
        stats.totalClicks += row.clicks
        stats.totalConversion += row.conversion
        return stats
      }, {
        totalSpend: 0,
        totalBilledCost: 0,
        totalCashSpend: 0,
        totalVoucherSpend: 0,
        totalRevenueDay0: 0,
        totalRevenueDay13: 0,
        totalImpressions: 0,
        totalClicks: 0,
        totalConversion: 0,
        avgCpa: 0
      })
      stats.avgCpa = stats.totalConversion > 0 ? stats.totalSpend / stats.totalConversion : 0
      return stats
    },
    sortReportRows(a, b) {
      if (this.isSplitByDate) {
        const dateCompare = String(b.statTimeDay || '').localeCompare(String(a.statTimeDay || ''))
        if (dateCompare !== 0) return dateCompare
      }
      return b.spend - a.spend
    },
    resetForm() {
      this.$refs.queryForm.resetFields()
      this.queryForm.dataSource = 'live'
      this.queryForm.advertiserIds = []
      this.queryForm.startDate = this.getDefaultStartDate()
      this.queryForm.endDate = this.getDefaultEndDate()
      this.queryForm.dateGroupMode = 'summary'
      this.tableData = []
      this.statsData = {
        totalSpend: 0,
        totalBilledCost: 0,
        totalCashSpend: 0,
        totalVoucherSpend: 0,
        totalRevenueDay0: 0,
        totalRevenueDay13: 0,
        totalImpressions: 0,
        totalClicks: 0,
        totalConversion: 0,
        avgCpa: 0
      }
      this.pagination.page = 1
      this.pagination.total = 0
      this.sortState = {
        field: '',
        order: ''
      }
    },
    exportData() {
      if (this.tableData.length === 0) {
        this.$message.warning('没有数据可导出')
        return
      }

      const exportData = this.tableData.map(item => {
        const row = {
          广告主ID: item.advertiserId,
          广告主名称: item.advertiserName,
          消耗: item.spend,
          净消耗: item.billedCost,
          现金消耗: item.cashSpend,
          赠款消耗: item.voucherSpend,
          展示次数: item.impressions,
          点击次数: item.clicks,
          点击率: this.formatPercent(item.ctr),
          转化数: item.conversion,
          CPA: item.cpa,
          第0天广告收入: item.revenueDay0,
          第13天广告收入: item.revenueDay13,
          第0天ROAS: item.roasDay0,
          第13天ROAS: item.roasDay13
        }
        if (this.isSavedData) {
          this.attributionDays.forEach(day => {
            row[`${day}日ROI`] = item[`roasDay${day}`]
            row[`${day}日收入`] = item[`revenueDay${day}`]
          })
        }
        if (this.isSplitByDate) {
          return { 日期: item.statTimeDay, ...row }
        }
        return row
      })

      const mode = this.isSplitByDate ? '按日期' : '汇总'
      this.exportToCSV(exportData, `广告主报表_${mode}_${this.queryForm.startDate}_${this.queryForm.endDate}.csv`)
    },
    exportToCSV(data, filename) {
      const headers = Object.keys(data[0])
      const csvContent = [
        headers.join(','),
        ...data.map(row => headers.map(header => this.escapeCsv(row[header])).join(','))
      ].join('\n')
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', filename)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      this.$message.success('导出成功')
    },
    escapeCsv(value) {
      const text = value === null || value === undefined ? '' : String(value)
      if (text.includes(',') || text.includes('"') || text.includes('\n')) {
        return `"${text.replace(/"/g, '""')}"`
      }
      return text
    },
    getTargetAdvertiserIds() {
      const selected = (this.queryForm.advertiserIds || []).filter(Boolean)
      if (selected.length > 0) return selected
      return this.advertiserList
        .map(item => item.advertiser_id)
        .filter(Boolean)
    },
    getAdvertiserName(advertiserId) {
      return this.advertiserNameMap[advertiserId] || advertiserId || '-'
    },
    formatAdvertiserOption(item) {
      const name = item.advertiser_name || item.advertiser_id
      return `${name} | ${item.advertiser_id}`
    },
    chunkArray(list, size) {
      const chunks = []
      for (let i = 0; i < list.length; i += size) {
        chunks.push(list.slice(i, i + size))
      }
      return chunks
    },
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.pagination.page = 1
      if (this.isSavedData) {
        this.loadSavedData()
      }
    },
    handleCurrentChange(val) {
      this.pagination.page = val
      if (this.isSavedData) {
        this.loadSavedData()
      }
    },
    handleSortChange({ prop, order }) {
      if (!this.isSavedData) return
      this.sortState = {
        field: prop || '',
        order: order || ''
      }
      this.pagination.page = 1
      this.loadSavedData()
    },
    backendSortField(prop) {
      if (prop === 'statTimeDay') return 'statDate'
      return prop || ''
    },
    handleDataSourceChange(value) {
      this.pagination.page = 1
      this.sortState = {
        field: '',
        order: ''
      }
      if (value === 'saved') {
        this.queryForm.dateGroupMode = 'day'
      }
    },
    getSummaryRow({ columns, data }) {
      const pageStats = this.calculateStats(data)
      return columns.map((column, index) => {
        if (index === 0) return '当前页合计'

        switch (column.property) {
          case 'spend':
            return `$${this.formatMoney(pageStats.totalSpend)}`
          case 'billedCost':
            return `$${this.formatMoney(pageStats.totalBilledCost)}`
          case 'cashSpend':
            return `$${this.formatMoney(pageStats.totalCashSpend)}`
          case 'voucherSpend':
            return `$${this.formatMoney(pageStats.totalVoucherSpend)}`
          case 'impressions':
            return this.formatNumber(pageStats.totalImpressions)
          case 'clicks':
            return this.formatNumber(pageStats.totalClicks)
          case 'ctr':
            return pageStats.totalImpressions > 0
              ? this.formatPercent(pageStats.totalClicks / pageStats.totalImpressions)
              : '0.00%'
          case 'conversion':
            return this.formatNumber(pageStats.totalConversion)
          case 'cpa':
            return `$${this.formatMoney(pageStats.avgCpa)}`
          case 'revenueDay0':
            return `$${this.formatMoney(pageStats.totalRevenueDay0)}`
          case 'revenueDay13':
            return `$${this.formatMoney(pageStats.totalRevenueDay13)}`
          case 'roasDay0':
            return pageStats.totalSpend > 0
              ? this.formatDecimal(pageStats.totalRevenueDay0 / pageStats.totalSpend, 2)
              : '0.00'
          case 'roasDay13':
            return pageStats.totalSpend > 0
              ? this.formatDecimal(pageStats.totalRevenueDay13 / pageStats.totalSpend, 2)
              : '0.00'
          default:
            return ''
        }
      })
    },
    getDefaultStartDate() {
      const date = new Date()
      date.setDate(date.getDate() - 1)
      return this.formatDate(date)
    },
    getDefaultEndDate() {
      return this.formatDate(new Date())
    },
    formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    toNumber(value) {
      if (value === null || value === undefined || value === '') return 0
      const numberValue = Number(value)
      return Number.isFinite(numberValue) ? numberValue : 0
    },
    formatMoney(value) {
      return this.formatDecimal(value, 2)
    },
    formatNumber(value) {
      return this.toNumber(value).toLocaleString('zh-CN', {
        maximumFractionDigits: 0
      })
    },
    formatDecimal(value, digits = 2) {
      return this.toNumber(value).toLocaleString('zh-CN', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits
      })
    },
    formatPercent(value) {
      return `${this.formatDecimal(this.toNumber(value) * 100, 2)}%`
    }
  }
}
</script>

<style scoped>
.advertiser-report {
  padding: 20px;
  background: #f5f7fa;
  color: #303133;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Microsoft YaHei", "Helvetica Neue", Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  min-height: calc(100vh - 40px);
}

.page-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.title-block h2 {
  margin: 0 0 8px;
  color: #303133;
  font-size: 22px;
  font-weight: 600;
}

.meta-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  color: #606266;
  font-size: 13px;
}

.toolbar-actions {
  flex-shrink: 0;
}

.filter-card {
  margin-bottom: 16px;
  border-radius: 6px;
}

.filter-form {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 0 12px;
}

.filter-form .el-form-item {
  margin-right: 0;
  margin-bottom: 12px;
}

.stats-row {
  margin-bottom: 16px;
}

.stat-card {
  border-radius: 6px;
  margin-bottom: 12px;
}

.stat-item {
  text-align: center;
  min-height: 56px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.stat-label {
  color: #909399;
  font-size: 14px;
  margin-bottom: 8px;
}

.stat-value {
  color: #303133;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
}

.stat-value.small {
  font-size: 18px;
}

.table-card {
  border-radius: 6px;
}

.table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.table-title {
  color: #303133;
  font-size: 16px;
  font-weight: 600;
}

.table-tag {
  margin-left: 8px;
}

.table-actions {
  color: #909399;
  font-size: 13px;
}

.muted-text {
  color: #909399;
}

.advertiser-info {
  line-height: 1.4;
}

.advertiser-name {
  color: #1f2937;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0;
}

.advertiser-id {
  color: #8a94a6;
  font-size: 12px;
  font-weight: 400;
  margin-top: 2px;
}

.metric-value.spend {
  color: #f56c6c;
  font-weight: 600;
}

.metric-value.revenue {
  color: #67c23a;
  font-weight: 600;
}

.report-table {
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}

.report-table /deep/ .el-table__header th {
  background: #fff;
  color: #7b8794;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.25;
  padding: 12px 0;
}

.report-table /deep/ .el-table__body td {
  color: #536173;
  font-size: 13px;
  line-height: 1.35;
  padding: 10px 0;
}

.report-table /deep/ .el-table__body .cell,
.report-table /deep/ .el-table__header .cell,
.report-table /deep/ .el-table__footer .cell {
  padding-left: 10px;
  padding-right: 10px;
}

.report-table /deep/ .el-table__footer td {
  background: #f7f9fc;
  color: #485465;
  font-size: 13px;
  font-weight: 600;
  padding: 11px 0;
}

.report-table /deep/ .sort-caret.ascending {
  border-bottom-color: #b8c2d1;
}

.report-table /deep/ .sort-caret.descending {
  border-top-color: #b8c2d1;
}

.table-pagination {
  margin-top: 20px;
  text-align: right;
}

@media (max-width: 768px) {
  .advertiser-report {
    padding: 12px;
  }

  .page-toolbar,
  .table-header {
    align-items: stretch;
    flex-direction: column;
  }

  .toolbar-actions {
    width: 100%;
  }

  .toolbar-actions .el-button {
    width: 100%;
  }
}
</style>
